// External libraries
import React, { Component } from 'react';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

// Zocdoc libraries
import { Button } from '@zocdoc-frontend-common/components-button';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';

import { isMobileBreakpoint } from '@zocdoc-frontend-common/utils-deviceutils';
import {
    Modal,
    ModalBodyCopy,
    ModalButtonContainer,
} from '@zocdoc-frontend-common/components-modal';
import { TOOLTIP } from './constants';

const StyledButton = styled(Button)`
    width: 100%;
`;

export class Tooltip extends Component {
    static propTypes = {
        onDisplayTooltip: PropTypes.func,
        onHideTooltip: PropTypes.func,
        onViewTooltip: PropTypes.func,
        showTooltip: PropTypes.bool,
        showTooltipOnFocus: PropTypes.bool,
        showTooltipOnHover: PropTypes.bool,
        tooltipColor: PropTypes.string,
        tooltipComponent: PropTypes.element,
        tooltipPosition: PropTypes.oneOf(Object.values(TOOLTIP)),
        showOnMobile: PropTypes.bool,
        useModalOnMobile: PropTypes.bool,
    };

    static defaultProps = {
        onDisplayTooltip: noop,
        onHideTooltip: noop,
        onViewTooltip: noop,
        showTooltip: false,
        showTooltipOnFocus: false,
        showTooltipOnHover: false,
        tooltipColor: 'white',
        tooltipPosition: TOOLTIP.RIGHT,
        showOnMobile: true,
        useModalOnMobile: true,
    };

    state = { active: false };

    render() {
        const {
            anchor,
            showTooltip,
            showTooltipOnFocus,
            showTooltipOnHover,
            showOutline,
        } = this.props;
        const contentVisible =
            showTooltipOnFocus || showTooltipOnHover
                ? this.state.active
                : showTooltip;
        const anchorStyle = showOutline ? {} : { outline: 'none' };
        const ref = React.createRef();

        return (
            <Wrapper
                data-test="tooltip-wrapper"
                onBlur={showTooltipOnFocus ? this.handleLeave : noop}
                onFocus={showTooltipOnFocus ? this.handleEnter : noop}
                onMouseLeave={showTooltipOnHover ? this.handleLeave : noop}
                onMouseEnter={showTooltipOnHover ? this.handleEnter : noop}
            >
                {contentVisible && this.renderTooltipOrModal()}
                <div
                    onClick={this.handleTouch}
                    onTouchEnd={this.handleTouch}
                    ref={ref}
                    role="button"
                    style={anchorStyle}
                    tabIndex="0"
                >
                    {anchor}
                </div>
            </Wrapper>
        );
    }

    renderTooltipOrModal() {
        const {
            children,
            showTooltipOnFocus,
            showTooltipOnHover,
            tooltipComponent,
            tooltipPosition,
            useModalOnMobile,
            ...props
        } = this.props;
        const useModal =
            (showTooltipOnHover || showTooltipOnFocus) &&
            useModalOnMobile &&
            isMobileBreakpoint();
        if (useModal) {
            return (
                <Modal
                    data-test="tooltip-modal-component"
                    onClickCancel={this.onClickHideModal}
                >
                    <ModalBodyCopy {...filterToValidAttributes(props)}>
                        {children}
                    </ModalBodyCopy>
                    <ModalButtonContainer>
                        <StyledButton
                            onClick={this.onClickHideModal}
                            style={Button.STYLE.TRANSPARENT}
                        >
                            Close
                        </StyledButton>
                    </ModalButtonContainer>
                </Modal>
            );
        } else {
            let ContentComponent = tooltipComponent;
            if (!tooltipComponent) {
                switch (tooltipPosition) {
                    case TOOLTIP.BOTTOM:
                        ContentComponent = BottomTooltipContent;
                        break;

                    case TOOLTIP.LEFT:
                        ContentComponent = LeftTooltipContent;
                        break;

                    case TOOLTIP.RIGHT:
                        ContentComponent = RightTooltipContent;
                        break;

                    default:
                        ContentComponent = TopTooltipContent;
                        break;
                }
            }

            return <ContentComponent {...props}>{children}</ContentComponent>;
        }
    }

    handleLeave = () => {
        const { showOnMobile, useModalOnMobile, onHideTooltip } = this.props;
        if (!isMobileBreakpoint() || (showOnMobile && useModalOnMobile)) {
            this.setState({ active: false });
            onHideTooltip();
        }
    };

    handleEnter = () => {
        if (
            !isMobileBreakpoint() ||
            (this.props.showOnMobile && !this.props.useModalOnMobile)
        ) {
            this.setState({ active: true });
            this.props.onDisplayTooltip();
            this.props.onViewTooltip();
        }
    };

    handleTouch = ev => {
        const { onDisplayTooltip, showOnMobile, useModalOnMobile } = this.props;
        if (
            !this.state.active &&
            isMobileBreakpoint() &&
            showOnMobile &&
            useModalOnMobile
        ) {
            ev && ev.preventDefault();
            this.setState({ active: true });
            onDisplayTooltip();
            this.props.onViewTooltip();
        }
    };

    onClickHideModal = () => this.setState({ active: false });
}

const Wrapper = styled.div`
    cursor: pointer;
    line-height: 0;
    position: relative;
`;

export const BaseTooltipCaret = css`
    border: solid transparent;
    content: '';
    pointer-events: none;
    position: absolute;
    height: 0;
    width: 0;
`;

const BaseTooltipContent = styled.div`
    background-color: ${p => p.tooltipColor};
    box-shadow: 0 0 1px 1px rgba(186, 186, 186, 0.5);
    cursor: default;
    display: inline-block;
    font-weight: normal;
    line-height: 1.3;
    padding: 10px 16px 13px;
    position: absolute;
    z-index: 2;

    &::before,
    &::after {
        ${BaseTooltipCaret}
    }
`;

export const LeftTooltipContent = styled(BaseTooltipContent)`
    margin-right: 21px;
    top: 50%;
    right: 100%;
    transform: translateY(-50%);

    &::before,
    &::after {
        left: 100%;
        top: 50%;
    }

    &::before {
        border-left-color: rgba(186, 186, 186, 0.5);
        border-width: 14px;
        margin-top: -14px;
    }

    &::after {
        border-left-color: ${p => p.tooltipColor};
        border-width: 12px;
        margin-top: -12px;
    }
`;

export const RightTooltipContent = styled(BaseTooltipContent)`
    margin-left: 21px;
    top: 50%;
    left: 100%;
    transform: translateY(-50%);

    &::before,
    &::after {
        right: 100%;
        top: 50%;
    }

    &::before {
        border-right-color: rgba(186, 186, 186, 0.5);
        border-width: 14px;
        margin-top: -14px;
    }

    &::after {
        border-right-color: ${p => p.tooltipColor};
        border-width: 12px;
        margin-top: -12px;
    }
`;

export const TopTooltipContent = styled(BaseTooltipContent)`
    bottom: 100%;
    margin-bottom: 21px;

    &::before,
    &::after {
        top: 100%;
        left: 50%;
    }

    &::before {
        border-top-color: rgba(186, 186, 186, 0.5);
        border-width: 14px;
        margin-left: -14px;
    }

    &::after {
        border-top-color: ${p => p.tooltipColor};
        border-width: 12px;
        margin-left: -12px;
    }
`;

export const BottomTooltipContent = styled(BaseTooltipContent)`
    top: 100%;
    margin-top: 21px;

    &::before,
    &::after {
        position: absolute;
        bottom: 100%;
        left: 50%;
    }

    &::before {
        border-bottom-color: rgba(186, 186, 186, 0.5);
        border-width: 14px;
        margin-left: -14px;
    }

    &::after {
        border-bottom-color: ${p => p.tooltipColor};
        border-width: 12px;
        margin-left: -12px;
    }
`;
